import { Directive, OnDestroy } from '@angular/core'
import { BehaviorSubject, Observable, Subscription, timer } from 'rxjs'
import { delayWhen, retryWhen, switchMap, take, takeUntil } from 'rxjs/operators'
import { Router } from '@angular/router'
import { Socket, io, ManagerOptions, SocketOptions } from 'socket.io-client'

import { JwtService } from '@app/modules/auth/jwt.service'
import { AuthService } from '@app/modules/auth/auth.service'
import { AuthResponse } from '@app/modules/auth/auth-response.model'
import { ApiService } from '@shared/services/api.service'
import { NetworkStatusService } from '@core/services/network-status.service'
import { NetworkStatus } from '@shared/models/network-status.model'
import { MapVehiclesService } from '../map-details/vehicles/map-vehicles.service'
import { DestroyService } from '@core/services/destroy.service'
import { MapListVehiclesService } from '@app/modules/client/map/map-details/vehicles/map-list-vehicles.service'
import { Vehicle } from '@shared/models/vehicle.model'
import { MapSettings, VehicleFilterValues } from '@app/modules/client/map/shared/models/map-settings.model'
import { MapApiService } from '@app/modules/client/map/map-api.service'
import { MapService } from '@app/modules/client/map/map.service'

export enum SocketStatus {
  Connect = 'connect',
  Disconnect = 'disconnect',
}

export interface SocketState {
  status: SocketStatus
  data?: unknown
}

@Directive()
export abstract class AbstractSocketDirective implements OnDestroy {
  baseUrlWS: string

  socket: Socket | null
  protected socketState = new BehaviorSubject<SocketState>({ status: SocketStatus.Disconnect })
  readonly socketState$ = this.socketState.asObservable()
  token: string

  private connected = false
  private readonly retryInMilliseconds = 2000
  private subscribeReconnect: Subscription
  private healthCheckSubscription: Subscription
  private hasTriedConnecting = false

  private readonly _defaultOptions: Partial<ManagerOptions & SocketOptions> = {
    transports: ['websocket'],
    path: '/socket.io',
  }

  get defaultOptions(): Partial<ManagerOptions & SocketOptions> {
    return this._defaultOptions
  }
  protected constructor(
    private readonly jwtService: JwtService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly apiService: ApiService,
    private networkStatusService: NetworkStatusService,
    protected readonly mapVehiclesService: MapVehiclesService,
    protected readonly mapListVehiclesService: MapListVehiclesService,
    protected readonly destroyService: DestroyService,
    protected readonly mapApiService: MapApiService,
    protected readonly mapService: MapService,
  ) {
    this.token = this.jwtService.getToken()
    this.baseUrlWS = apiService.baseUrlWS
    const socketPath = '/socket.io'

    this._defaultOptions = {
      path: socketPath,
      transports: ['websocket'],
      query: { token: this.token },
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 5000,
      reconnectionDelayMax: 10000,
      timeout: 20000,
    }
  }

  abstract connectToNamespace()

  ngOnDestroy(): void {
    this.close()
  }

  close(): void {
    if (this.socket) {
      this.socket.removeAllListeners()
    }

    this.connected = false
    this.socketState.next({ status: SocketStatus.Disconnect })
    this.subscribeReconnect?.unsubscribe()
    this.healthCheckSubscription?.unsubscribe()
  }

  isConnected(state: SocketState): boolean {
    return state.status === SocketStatus.Connect
  }

  protected isSocketExist(): void {
    if (!this.socket || !this.socket.connected) {
      this.connect()
    }
  }

  protected connect(): void {
    if (this.socket?.connected || this.socket) return
    if (this.hasTriedConnecting) return

    this.hasTriedConnecting = true
    this.connectToNamespace()

    if (!this.socket) return

    this.socket.off?.('connect', this.onSocketConnect)
    this.socket.off?.('reconnect', this.onSocketReconnect)
    this.socket.off?.('disconnect', this.onSocketDisconnect)
    this.socket.off?.('error', this.onSocketError)

    this.socket.on('connect', this.onSocketConnect)
    this.socket.on('reconnect', this.onSocketReconnect)
    this.socket.on('disconnect', this.onSocketDisconnect)
    this.socket.on('error', this.onSocketError)

    this.socket.on('connect_error', (error: any) => {
      // console.error('[Socket] Connect Error:', error);
    })

    this.socket.on('disconnect', (reason: any) => {
      //console.warn('[Socket] Disconnected due to:', reason);
    })

    this.socket.on('error', (error: any) => {
      // console.error('[Socket] General Error:', error);
    })
  }

  protected onSocketConnect = (): void => {
    this.connected = true
    this.socketState.next({ status: SocketStatus.Connect })
    this.networkStatusService.setStatus(NetworkStatus.online)
  }

  protected onSocketReconnect = (): void => {
    if (this.socket.connected) return
    this.reconnect()
  }

  protected onSocketDisconnect = (e): void => {
    if (e === 'transport close') return
    this.networkStatusService.setStatus(NetworkStatus.offline)
    this.connected = false
    this.socketState.next({ status: SocketStatus.Disconnect })
    this.healthCheck()
  }

  protected onSocketError = (error: any): void => {
    // console.error('Socket error:', error)
    const refreshToken: string = this.jwtService.getRefreshToken()

    if (error === 'AUTH_FAILED') {
      if (!refreshToken) {
        // console.warn('No refresh token, logging out...')
        this.authService.logout().subscribe((response) => {
          this.authService.purgeAuth()

          if (response?.redirectUrl) {
            this.router.navigateByUrl('/login')
          } else {
            this.router.navigate(['/login'])
          }
        })
      } else {
        // console.log('Attempting token refresh...')
        this.refreshToken(refreshToken)
      }
    } else {
      //console.warn('Non-auth error, disconnecting socket...')
      this.socket?.disconnect()
      this.connected = false
    }
  }

  private refreshToken(refreshToken: string): void {
    this.authService
      .refreshToken(refreshToken)
      .pipe(takeUntil(this.destroyService))
      .subscribe(
        (authResponse: AuthResponse) => {
          //console.log('Token refreshed')
          this.jwtService.saveToken(authResponse.token)
          this.jwtService.saveRefreshToken(authResponse.refreshToken)
          this.socket.io.opts.query = { token: authResponse.token }
          this.socket.disconnect()
          this.socket.connect()
        },
        (error) => {
          //console.error('Failed to refresh token, logging out...')
          this.authService.purgeAuth()
          this.router.navigate(['/login'])
        },
      )
  }

  private reconnect = (): void => {
    const reconnectVehiclesSubscribe = (vehicles: Vehicle[]): void => {
      this.mapListVehiclesService.setVehicles(vehicles)
      this.subscribeReconnect?.unsubscribe()
      this.mapService.setIsLoading(false)
    }

    const handleError = (): void => {
      this.subscribeReconnect?.unsubscribe()
      if (this.jwtService.getRefreshToken()) {
        setTimeout(() => this.reconnect(), 10000)
      }
    }

    this.mapService.setIsLoading(true)
    this.subscribeReconnect = this.mapApiService
      .getMapSettings()
      .pipe(switchMap(this.getVehiclesWithMapSettings), takeUntil(this.destroyService))
      .subscribe(reconnectVehiclesSubscribe, handleError)
  }

  private healthCheckSuccess = (): void => {
    this.connected = true

    if (this.socket) {
      this.socket.connect()
    } else {
      this.connect()
    }

    this.reconnect()
  }

  private healthCheck = (): void => {
    this.healthCheckSubscription?.unsubscribe()
    if (!this.connected) {
      this.healthCheckSubscription = this.apiService
        .get('/health')
        .pipe(
          take(1),
          retryWhen((errors) => errors.pipe(delayWhen(() => timer(this.retryInMilliseconds)))),
          takeUntil(this.destroyService),
        )
        .subscribe(this.healthCheckSuccess)
    }
  }

  private getVehiclesWithMapSettings = (mapSettings: MapSettings): Observable<Vehicle[]> => {
    const { fullSearch, groups, depot } = mapSettings.sidebar.filters as VehicleFilterValues
    return this.mapVehiclesService.getVehicles({
      fullSearch,
      groups,
      depot,
    })
  }
}
